import { FC, SVGProps, useEffect, useRef, useState } from 'react';
import { Easing, motion, MotionStyle } from 'motion/react';
import FaceProud2 from '../../assets/Faceproud2.svg?react';
import Fire from '../../assets/Fire.svg?react';
import PracticeProblemsAchievementTooltipTail from './PracticeProblemsAchievementTooltipTail';

export type AchievementCriteria = 'across' | 'hard';

interface AchievementContent {
  icon: FC<SVGProps<SVGSVGElement>>;
  title: string;
  text: string;
}

const CRITERIA_CONTENT: Record<AchievementCriteria, AchievementContent> = {
  across: {
    icon: FaceProud2,
    title: 'Achievement Unlocked!',
    text: '1 Easy, 1 Medium, and 1 Hard = Skill Challenge Unlocked!',
  },
  hard: {
    icon: Fire,
    title: 'Fearless!',
    text: 'You unlocked this Skill Challenge by getting 2 Hard questions right!',
  },
};

const ENTRANCE_EASE: Easing = [0.34, 1.56, 0.64, 1];
// The bubble grows out of whatever sits right below its tail, so it scales from that bottom-right
// corner instead of from its own centre.
const ENTRANCE_ORIGIN: MotionStyle = { originX: 0.92, originY: 1 };
const HIDE_AFTER_MS = 12000;

interface Props {
  criteria: AchievementCriteria;
  delay?: number;
  customStyle?: MotionStyle;
  onAnimationEnd?: () => void;
}

export const PracticeProblemsAchievementTooltip: FC<Props> = ({
  criteria,
  delay = 0.3,
  customStyle,
  onAnimationEnd,
}) => {
  const [visible, setVisible] = useState<boolean>(true);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The parent may unmount the tooltip before the timer fires (for instance when the student moves
  // to the next problem), so the pending timeout is cleared on unmount.
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  if (!visible) return null;

  const { icon: Icon, title, text } = CRITERIA_CONTENT[criteria];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0, rotateY: -90 }}
      animate={{ opacity: 1, scale: 1, rotateY: 0 }}
      transition={{ duration: 1, delay, ease: ENTRANCE_EASE }}
      style={{ ...ENTRANCE_ORIGIN, ...customStyle }}
      onAnimationComplete={() => {
        hideTimeoutRef.current = setTimeout(() => setVisible(false), HIDE_AFTER_MS);
        if (onAnimationEnd) {
          onAnimationEnd();
        }
      }}
      className="tw-absolute tw-z-[3] tw-flex tw-w-[420px] tw-flex-col tw-items-center tw-gap-2 tw-rounded-3xl tw-border-[4px] tw-border-solid tw-border-neutral-100 tw-bg-neutral-0 tw-px-6 tw-py-4"
    >
      <span className="tw-flex tw-h-24 tw-w-24 tw-items-center tw-justify-center tw-rounded-full tw-bg-neutral-100">
        {/* The asset's artboard is narrower than its background circle, so the rounding is applied
            here to clip it back into a clean circle. */}
        <Icon className="tw-h-[5.25rem] tw-w-[5.25rem] tw-rounded-full" aria-hidden="true" />
      </span>
      <h3 className="tw-m-0 tw-text-center tw-font-atkinson tw-text-lg tw-font-bold tw-italic tw-text-neutral-950">
        {title}
      </h3>
      <p className="tw-m-0 tw-text-center tw-font-atkinson tw-text-lg tw-font-normal tw-italic tw-text-neutral-950">
        {text}
      </p>
      <PracticeProblemsAchievementTooltipTail />
    </motion.div>
  );
};
